import i18n, { type i18n as I18nInstance } from "i18next";
import { initReactI18next } from "react-i18next";

// Translation catalogues live in public/locales/{lang}/translation.json so they are
// also fetchable at runtime; they are bundled here for SSR + instant switching.
import en from "../../public/locales/en/translation.json";
import ar from "../../public/locales/ar/translation.json";
import es from "../../public/locales/es/translation.json";
import zh from "../../public/locales/zh/translation.json";
import fr from "../../public/locales/fr/translation.json";

import enChrome from "../../public/locales/en/chrome.json";
import arChrome from "../../public/locales/ar/chrome.json";
import esChrome from "../../public/locales/es/chrome.json";
import zhChrome from "../../public/locales/zh/chrome.json";
import frChrome from "../../public/locales/fr/chrome.json";

import enLegal from "../../public/locales/en/legal.json";
import arLegal from "../../public/locales/ar/legal.json";
import esLegal from "../../public/locales/es/legal.json";
import zhLegal from "../../public/locales/zh/legal.json";
import frLegal from "../../public/locales/fr/legal.json";

import enAccount from "../../public/locales/en/account.json";
import arAccount from "../../public/locales/ar/account.json";
import esAccount from "../../public/locales/es/account.json";
import zhAccount from "../../public/locales/zh/account.json";
import frAccount from "../../public/locales/fr/account.json";

import enPortal from "../../public/locales/en/portal.json";
import arPortal from "../../public/locales/ar/portal.json";
import esPortal from "../../public/locales/es/portal.json";
import zhPortal from "../../public/locales/zh/portal.json";
import frPortal from "../../public/locales/fr/portal.json";

import enPages from "../../public/locales/en/pages.json";
import arPages from "../../public/locales/ar/pages.json";
import esPages from "../../public/locales/es/pages.json";
import zhPages from "../../public/locales/zh/pages.json";
import frPages from "../../public/locales/fr/pages.json";

export const NAMESPACES = ["translation", "chrome", "legal", "account", "portal", "pages"] as const;

export const SUPPORTED_LANGUAGES = [
  { code: "en", name: "English", dir: "ltr" },
  { code: "ar", name: "العربية", dir: "rtl" },
  { code: "es", name: "Español", dir: "ltr" },
  { code: "zh", name: "中文", dir: "ltr" },
  { code: "fr", name: "Français", dir: "ltr" },
] as const;

export type LanguageCode = (typeof SUPPORTED_LANGUAGES)[number]["code"];

export const DEFAULT_LANGUAGE: LanguageCode = "en";
export const LANGUAGE_STORAGE_KEY = "swl:language";

export function isSupportedLanguage(value: string | null | undefined): value is LanguageCode {
  if (!value) return false;
  return SUPPORTED_LANGUAGES.some((l) => l.code === value);
}

/** Maps a BCP-47 tag such as "ar-AE" or "zh-Hans-CN" onto a supported language. */
export function normalizeLanguage(tag: string | null | undefined): LanguageCode | null {
  if (!tag) return null;
  const base = tag.toLowerCase().split(/[-_]/)[0];
  return isSupportedLanguage(base) ? base : null;
}

export function directionFor(code: string): "ltr" | "rtl" {
  return code === "ar" ? "rtl" : "ltr";
}

/** localStorage preference first, then the browser/OS locale, then English. */
export function detectLanguage(): LanguageCode {
  if (typeof window === "undefined") return DEFAULT_LANGUAGE;

  try {
    const stored = window.localStorage.getItem(LANGUAGE_STORAGE_KEY);
    if (isSupportedLanguage(stored)) return stored;
  } catch {
    /* storage blocked — fall through to detection */
  }

  const candidates: (string | undefined)[] = [
    ...(navigator.languages ?? []),
    navigator.language,
    Intl.DateTimeFormat().resolvedOptions().locale,
  ];

  for (const candidate of candidates) {
    const match = normalizeLanguage(candidate);
    if (match) return match;
  }

  return DEFAULT_LANGUAGE;
}

export function getI18n(): I18nInstance {
  if (!i18n.isInitialized) {
    void i18n.use(initReactI18next).init({
      resources: {
        en: { translation: en, chrome: enChrome, legal: enLegal, account: enAccount, portal: enPortal, pages: enPages },
        ar: { translation: ar, chrome: arChrome, legal: arLegal, account: arAccount, portal: arPortal, pages: arPages },
        es: { translation: es, chrome: esChrome, legal: esLegal, account: esAccount, portal: esPortal, pages: esPages },
        zh: { translation: zh, chrome: zhChrome, legal: zhLegal, account: zhAccount, portal: zhPortal, pages: zhPages },
        fr: { translation: fr, chrome: frChrome, legal: frLegal, account: frAccount, portal: frPortal, pages: frPages },
      },
      ns: NAMESPACES as unknown as string[],
      lng: DEFAULT_LANGUAGE,
      fallbackLng: DEFAULT_LANGUAGE,
      supportedLngs: SUPPORTED_LANGUAGES.map((l) => l.code),
      defaultNS: "translation",
      interpolation: { escapeValue: false },
      returnEmptyString: false,
      react: { useSuspense: false },
    });
  }
  return i18n;
}

export default i18n;
